import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@opendate-app/setlist";
import StatusBadge from "../../../shared/StatusBadge";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import MetaCapiModal from "./MetaCapiModal";

const ConnectionBadge = ({ connectedCount, venueCount }) => {
  if (connectedCount === 0) return null;

  if (venueCount === 1) return <StatusBadge status="connected" label="Connected" />;

  return (
    <StatusBadge
      status={connectedCount === venueCount ? 'connected' : 'pending'}
      label={`${connectedCount} of ${venueCount} venues`}
    />
  );
};

ConnectionBadge.propTypes = {
  connectedCount: PropTypes.number.isRequired,
  venueCount: PropTypes.number.isRequired,
};

const MetaCapiConnectionRow = ({ venues, selectedVenueOwnership, editingAllVenues, teamId, onVenuesChange }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const connectedCount = venues.filter((venue) => venue.meta_capi_connected).length;
  const targetVenues = editingAllVenues ? venues : [selectedVenueOwnership].filter(Boolean);
  const missingPixelId = targetVenues.some((venue) => !venue.facebook_pixel_id);
  const hasTarget = targetVenues.length > 0;
  const connected = hasTarget && targetVenues.every((venue) => venue.meta_capi_connected);

  const modalTarget = editingAllVenues
    ? { id: 'all', name: `all ${venues.length} venues`, venueCount: venues.length }
    : selectedVenueOwnership;

  const tokenLastFour = editingAllVenues ? null : selectedVenueOwnership
    && selectedVenueOwnership.meta_capi_token_last_four;

  const description = () => {
    if (missingPixelId) {
      return 'Add a Facebook pixel ID first — server-side events send to the same ID';
    }
    if (tokenLastFour) return `Token ending ••••${tokenLastFour}`;

    return 'Server-side purchase tracking, so blocked pixels still attribute sales';
  };

  const handleRemove = async () => {
    if (removing) return;

    setRemoving(true);
    try {
      const { data } = await client.delete(
        `/teams/${teamId}/facebook_pixels/${selectedVenueOwnership.meta_capi_pixel_id}`
      );
      toastSuccess(`Conversions API disconnected for ${selectedVenueOwnership.name}.`);
      onVenuesChange(data);
    } catch (error) {
      toastError('Could not disconnect. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <tr>
        <td style={{ paddingLeft: 24 }}>
          <div className="team-member-info">
            <div
              className="team-avatar"
              style={{
                borderRadius: 8,
                backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="fas fa-server" style={{ fontSize: 20, color: '#1877F2' }} />
            </div>
            <div className="team-member-details">
              <span className="team-member-name">Meta Conversions API</span>
              <span className="team-member-email">{description()}</span>
            </div>
          </div>
        </td>
        <td>
          <ConnectionBadge connectedCount={connectedCount} venueCount={venues.length} />
        </td>
        <td style={{ textAlign: 'right', paddingRight: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
            {connected && !editingAllVenues && (
              <Button variant="secondary" size="sm" disabled={removing} onClick={handleRemove}>
                {removing ? 'Removing…' : 'Remove'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasTarget || missingPixelId}
              onClick={() => setModalOpen(true)}
            >
              {connected ? 'Replace token' : 'Connect'}
            </Button>
          </div>
        </td>
      </tr>
      {modalOpen && hasTarget && (
        <tr>
          <td colSpan={3} style={{ padding: 0 }}>
            <MetaCapiModal
              target={modalTarget}
              teamId={teamId}
              onClose={() => setModalOpen(false)}
              onSaved={onVenuesChange}
            />
          </td>
        </tr>
      )}
    </>
  );
};

MetaCapiConnectionRow.propTypes = {
  venues: PropTypes.array.isRequired,
  selectedVenueOwnership: PropTypes.object,
  editingAllVenues: PropTypes.bool.isRequired,
  teamId: PropTypes.number.isRequired,
  onVenuesChange: PropTypes.func.isRequired,
};

export default MetaCapiConnectionRow;
